import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router';

import { Icons, Modal, PageTitle, Table } from '@components/common';

import { useMutateConsistencyCheckRanAssignTemplate } from '@hooks/cmModule/consistencyCheckRan/useMutateConsistencyCheckRanAssignTemplate';
import { useMutateConsistencyCheckRanDisableMonitoring } from '@hooks/cmModule/consistencyCheckRan/useMutateConsistencyCheckRanDisableMonitoring';
import { useMutateConsistencyCheckRanEnableMonitoring } from '@hooks/cmModule/consistencyCheckRan/useMutateConsistencyCheckRanEnableMonitoring';
import { useMutateConsistencyCheckRanUnassignTemplate } from '@hooks/cmModule/consistencyCheckRan/useMutateConsistencyCheckRanUnassignTemplate';
import useQueryConsistencyCheckRanConsistencyAssignedTemplates from '@hooks/cmModule/consistencyCheckRan/useQueryConsistencyCheckRanConsistencyAssignedTemplates';
import useQueryConsistencyCheckRanConsistencyStatuses from '@hooks/cmModule/consistencyCheckRan/useQueryConsistencyCheckRanConsistencyStatuses';
import useQueryConsistencyCheckRanMonitoringStatuses from '@hooks/cmModule/consistencyCheckRan/useQueryConsistencyCheckRanMonitoringStatuses';
import useQueryConsistencyCheckRanTable from '@hooks/cmModule/consistencyCheckRan/useQueryConsistencyCheckRanTable';
import { useSearchParamsQuery } from '@hooks/useSearchParamsQuery';

import { auth } from '@store/selectors';
import { useAppSelector } from '@store/store';

import { formatDate, startOfDayInMilliseconds } from '@utils/date';

import { IConsistencyCheckRan } from '@interfaces/cmModule/consistencyCheck';
import { TSelectOption } from '@interfaces/components/componentTypes';
import {
	ISortBy,
	ITableDatepickerFilter,
	ITablePagination,
	ITableSort,
	TTableFilter,
} from '@interfaces/components/table';

import { defaultMeta } from '@enums/common';

import { MIN_FILTER_WIDTH } from '@constants/common';
import { MODAL_WIDTH_455 } from '@constants/modals';
import { responseMessages } from '@constants/responseMessages';
import { routes } from '@constants/routes';
import { TableFilterTypes } from '@constants/table';
import { consistencyCheckPageTitle } from '../constants';
import { ASSIGN_REFERENCE_TEMPLATE } from './constants';

import AssignConsistencyCheckTemplateForm from './AssignConsistencyCheckTemplateForm';
import ConsistencyCheckActions from './ConsistencyCheckActions';
import { getColumns } from './utils';

import styles from './ConsistencyCheckTable.module.scss';

const ConsistencyCheckTable = () => {
	const navigate = useNavigate();
	const templateId = useSearchParamsQuery().get('templateId');

	const {
		accessPermission: {
			networkOperatorCmModuleAdmin,
			networkOperatorCmModuleEditor,
		},
	} = useAppSelector(auth);

	const [
		selectedConsistencyCheckRan,
		setSelectedConsistencyCheckRan,
	] = useState<IConsistencyCheckRan | undefined>(undefined);

	const [selectedIds, setSelectedIds] = useState<string[]>([]);

	const [isOpenAssignTemplateModal, setIsOpenAssignTemplateModal] = useState(false);

	const [page, setPage] = useState(defaultMeta.page);
	const [pageSize, setPageSize] = useState(defaultMeta.pageSize);
	const [sortDirection, setSortDirection] = useState(false);
	const [sortFieldName, setSortFieldName] = useState<string | null>(null);

	const [assignedReference, setAssignedReference] = useState<TSelectOption<string>>(null);
	const [selectedConsistencyStatus, setSelectedConsistencyStatus] = useState<TSelectOption<string>>(null);
	const [selectedMonitoringStatus, setSelectedMonitoringStatus] = useState<TSelectOption<string>>(null);
	const [selectedOperationalWindow, setSelectedOperationalWindow] = useState<TSelectOption<string>>(null);
	const [lastCheckedFrom, setLastCheckedFrom] = useState<number | null>(null);
	const [lastCheckedTo, setLastCheckedTo] = useState<number | null>(null);

	const isViewerOnly = !networkOperatorCmModuleEditor && !networkOperatorCmModuleAdmin;

	const openAssignTemplateModal = useCallback(() => setIsOpenAssignTemplateModal(true), []);
	const closeAssignTemplateModal = useCallback(() => {
		setIsOpenAssignTemplateModal(false);
	}, []);

	const filter = useMemo(() => ({
		goldenTemplateId: assignedReference?.value || undefined,
		consistencyStatus: selectedConsistencyStatus?.value || undefined,
		monitoringStatus: selectedMonitoringStatus?.value || undefined,
		lastCheckTimeToText: lastCheckedTo ? formatDate(startOfDayInMilliseconds(lastCheckedTo)) : undefined,
		lastCheckTimeFromText: lastCheckedFrom ? formatDate(startOfDayInMilliseconds(lastCheckedFrom)) : undefined,
	}), [
		assignedReference,
		selectedConsistencyStatus,
		selectedMonitoringStatus,
		lastCheckedTo,
		lastCheckedFrom,
	]);

	const {
		consistencyRanTableData,
		countOfRecords,
		isLoadingConsistencyRanTable,
	} = useQueryConsistencyCheckRanTable({
		page,
		pageSize,
		sortDirection,
		sortFieldName,
		...filter,
	});

	const {
		disableMonitoring,
		isLoadingDisableMonitoring,
	} = useMutateConsistencyCheckRanDisableMonitoring();

	const {
		enableMonitoring,
		isLoadingEnableMonitoring,
	} = useMutateConsistencyCheckRanEnableMonitoring();

	const {
		assignTemplate,
		isLoadingAssignTemplate,
	} = useMutateConsistencyCheckRanAssignTemplate({
		onSuccess: closeAssignTemplateModal,
		message: selectedConsistencyCheckRan?.goldenTemplateId
			? responseMessages.reassignConsistencyCheckRanTemplate
			: responseMessages.assignConsistencyCheckRanTemplate,
	});

	const {
		unassignTemplate,
		isLoadingUnassignTemplate,
	} = useMutateConsistencyCheckRanUnassignTemplate({
		onSuccess: closeAssignTemplateModal,
	});

	const {
		consistencyOptions,
		isLoadingConsistencyOptions,
	} = useQueryConsistencyCheckRanConsistencyStatuses();

	const {
		assignedTemplateOptions,
		isLoadingAssignedTemplateOptions,
	} = useQueryConsistencyCheckRanConsistencyAssignedTemplates();

	const {
		monitoringOptions,
		isLoadingMonitoringOptions,
	} = useQueryConsistencyCheckRanMonitoringStatuses();

	const toggleAvailability = useCallback((id: string, isEnabled: boolean) => {
		if (!isEnabled) {
			enableMonitoring({ id });
		} else {
			disableMonitoring({ id });
		}
	}, [enableMonitoring, disableMonitoring]);

	const navigateConsistencyHistory = useCallback((id: string) => {
		navigate(`${routes.adminPanel.index}${routes.cmModule.consistencyCheck}/${id}`);
	}, [navigate]);

	const columns = getColumns({
		openAssignTemplateModal,
		setSelectedConsistencyCheckRan,
		toggleAvailability,
		navigateConsistencyHistory,
	});

	const pageHandler = useCallback((page: number) => setPage(page), []);
	const pageSizeHandler = useCallback((pageSize: number) => {
		setPageSize(pageSize);
		setPage(defaultMeta.page);
	}, []);

	const pagination: ITablePagination = useMemo(() => ({
		countOfRecords: countOfRecords,
		forcePage: page,
		size: pageSize,
		pageHandler,
		pageSizeHandler,
	}), [
		page,
		pageSize,
		countOfRecords,
		pageHandler,
		pageSizeHandler,
	]);

	const handleSort = useCallback((sortBy: Array<ISortBy>) => {
		setSortDirection(!!sortBy[0]?.desc);
		setSortFieldName(sortBy[0]?.id);
		setPage(defaultMeta.page);
	}, []);

	const handleAssignedReferenceChange = useCallback((option: TSelectOption<string>) => {
		setAssignedReference(option);
		setPage(defaultMeta.page);
	}, []);

	const handleConsistencyStatusChange = useCallback((option: TSelectOption<string>) => {
		setSelectedConsistencyStatus(option);
		setPage(defaultMeta.page);
	}, []);

	const handleMonitoringStatusChange = useCallback((option: TSelectOption<string>) => {
		setSelectedMonitoringStatus(option);
		setPage(defaultMeta.page);
	}, []);

	const handleOperationalWindowChange = useCallback((option: TSelectOption<string>) => {
		setSelectedOperationalWindow(option);
		setPage(defaultMeta.page);
	}, []);

	const onChangeLastCheckedFrom = useCallback((value: number | null) => {
		setLastCheckedFrom(value);
	}, []);

	const onChangeLastCheckedTo = useCallback((value: number | null) => {
		setLastCheckedTo(value);
	}, []);

	const currentPageIds = useMemo((): string[] => {
		return consistencyRanTableData.map((item) => item.id);
	}, [consistencyRanTableData]);

	const filters: TTableFilter[] = useMemo(() => {
		return [
			{
				name: 'goldenTemplateId',
				placeholder: 'Assigned reference',
				option: assignedReference,
				options: assignedTemplateOptions,
				minWidth: MIN_FILTER_WIDTH,
				type: TableFilterTypes.SELECT,
				setState: handleAssignedReferenceChange,
				defaultValue: null,
			},
			{
				name: 'consistencyStatus',
				placeholder: 'Consistency status',
				option: selectedConsistencyStatus,
				options: consistencyOptions,
				minWidth: MIN_FILTER_WIDTH,
				type: TableFilterTypes.SELECT,
				setState: handleConsistencyStatusChange,
				defaultValue: null,
			},
			{
				name: 'monitoringStatus',
				placeholder: 'Monitoring status',
				option: selectedMonitoringStatus,
				options: monitoringOptions,
				minWidth: MIN_FILTER_WIDTH,
				type: TableFilterTypes.SELECT,
				setState: handleMonitoringStatusChange,
				defaultValue: null,
			},
			{
				name: 'operationalWindow',
				placeholder: 'Operational window',
				option: selectedOperationalWindow,
				options: [],
				minWidth: MIN_FILTER_WIDTH,
				type: TableFilterTypes.SELECT,
				setState: handleOperationalWindowChange,
				defaultValue: null,
			},
		];
	}, [
		assignedReference,
		selectedConsistencyStatus,
		selectedMonitoringStatus,
		selectedOperationalWindow,
		consistencyOptions,
		monitoringOptions,
		assignedTemplateOptions,
		handleAssignedReferenceChange,
		handleConsistencyStatusChange,
		handleMonitoringStatusChange,
		handleOperationalWindowChange,
	]);

	const dateFilters: ITableDatepickerFilter[] = useMemo(() => [
		{
			name: 'from',
			label: 'Last checked from',
			handleChange: onChangeLastCheckedFrom,
			selected: lastCheckedFrom ? new Date(lastCheckedFrom) : null,
			disableFutureDates: true,
		},
		{
			name: 'to',
			label: 'to',
			handleChange: onChangeLastCheckedTo,
			selected: lastCheckedTo ? new Date(lastCheckedTo) : null,
			disableFutureDates: true,
		},
	], [
		lastCheckedFrom,
		lastCheckedTo,
		onChangeLastCheckedFrom,
		onChangeLastCheckedTo,
	]);

	const sort: ITableSort = useMemo(() => ({
		initialState: [{ id: sortFieldName, desc: sortDirection }],
		handleSort,
	}), [sortFieldName, sortDirection, handleSort]);

	useEffect(() => {
		if (templateId) {
			const selectedTemplate = assignedTemplateOptions.find((option) => option?.value === templateId);

			setAssignedReference(selectedTemplate || null);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [
		templateId,
		assignedTemplateOptions.length,
	]);

	const isLoading =
		isLoadingConsistencyRanTable ||
		isLoadingDisableMonitoring ||
		isLoadingEnableMonitoring ||
		isLoadingConsistencyOptions ||
		isLoadingMonitoringOptions ||
		isLoadingAssignedTemplateOptions;

	return (
		<div className={styles.wrapper}>
			<Modal
				isOpen={isOpenAssignTemplateModal}
				onClose={closeAssignTemplateModal}
				title={ASSIGN_REFERENCE_TEMPLATE}
				width={MODAL_WIDTH_455}
			>
				<AssignConsistencyCheckTemplateForm
					assignTemplate={assignTemplate}
					assignedTemplateId={selectedConsistencyCheckRan?.goldenTemplateId}
					id={selectedConsistencyCheckRan?.id}
					isLoading={isLoadingAssignTemplate || isLoadingUnassignTemplate}
					unassignTemplate={unassignTemplate}
				/>
			</Modal>
			<PageTitle
				icon={Icons.configurationIcon()}
				title={consistencyCheckPageTitle}
			/>
			<div className={styles.tableContainer}>
				<Table
					addTooltipText='Add new template'
					columns={columns}
					customLeftAction={!isViewerOnly && (
							<ConsistencyCheckActions
								currentPageIds={currentPageIds}
								filter={filter}
								selectedIds={selectedIds}
							/>
					)}
					data={consistencyRanTableData}
					dateFilters={dateFilters}
					filters={filters}
					isLoading={isLoading}
					isOpenFilters={!!templateId}
					isPinColumns
					isWithoutActions
					isWithoutDeleteAction
					pagination={pagination}
					setSelectedIds={setSelectedIds}
					sort={sort}
					stickyLeftColumnIndexes={[0, 1, 2]}
				/>
			</div>
		</div>
	);
};

export default ConsistencyCheckTable;
